import React, { useState, useRef, useEffect } from 'react';

interface DropdownProps {
    label: string;
    items: { id: string; name: string }[];
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
    label,
    items,
    selectedId,
    onSelect,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Текущий выбранный элемент
    const selectedItem = selectedId === null
        ? { id: null, name: 'Все' }
        : items.find(item => item.id === selectedId) || { id: null, name: 'Все' };

    // Закрыть выпадающий список при клике вне его
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                type="button"
                className={`flex items-center justify-between w-full px-4 py-2.5 text-sm 
                           rounded-lg shadow-sm hover:shadow-md transition-all duration-300
                           border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-400
                           ${isOpen ? 'bg-primary-50 border-primary-200' : 'bg-white border-gray-200'}`}
                style={{ height: '45px' }}
            >
                <div className="flex items-center">
                    <span className="mr-2 text-gray-600">{label}:</span>
                    <span className={`font-medium text-yellow-500`}>
                        {selectedItem.name}
                    </span>
                </div>
                <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-primary"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
            </button>

            <div
                className={`absolute left-0 right-0 z-30 mt-1 overflow-hidden transition-all duration-300 transform origin-top
                            ${isOpen
                        ? 'opacity-100 scale-y-100 max-h-60'
                        : 'opacity-0 scale-y-0 max-h-0'}`}
            >
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                    <div className="max-h-60 overflow-y-auto py-1 dropdown-scrollbar">
                        <button
                            className={`flex items-center w-full px-4 py-2.5 text-sm hover:bg-primary-50 transition-colors
                                       ${selectedId === null
                                    ? 'bg-primary-50 text-yellow-500 font-medium'
                                    : 'text-black'}`}
                            onClick={() => {
                                onSelect(null);
                                setIsOpen(false);
                            }}
                        >
                            <span className="mr-2">Все</span>
                            {selectedId === null && (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </button>
                        {items.map((item) => (
                            <button
                                key={item.id}
                                className={`flex items-center w-full px-4 py-2.5 text-sm hover:bg-primary-50 transition-colors
                                           ${selectedId === item.id
                                        ? 'bg-primary-50 text-yellow-500 font-medium'
                                        : 'text-black'}`}
                                onClick={() => {
                                    onSelect(item.id);
                                    setIsOpen(false);
                                }}
                            >
                                <span className="mr-2">{item.name}</span>
                                {selectedId === item.id && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary-600" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Затемнение фона при открытом списке */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-10 z-20"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

export default Dropdown; 